import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const useStyles = makeStyles((theme) => ({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    content: {
        position: 'relative',
        width: '22rem',
        background: 'linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        [theme.breakpoints.up("xl")]: {
            width: '26.5rem',
            padding: '1.8rem',
        },
    },
    animateIn: {
        animation: '$scaleInBounce 0.5s ease-in-out',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },
    title: {
        fontSize: '1.2rem',
        fontWeight: 525,
        color: '#ffffff',
        textAlign: 'center',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.5rem',
        },
    },
    inputContainer: {
        width: '100%',
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        [theme.breakpoints.up("xl")]: {
            marginTop: '1.8rem',
            marginBottom: '1.8rem',
        },
    },
    inputCodeLobby: {
        width: '80%',
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '10px',
            transition: 'all 0.3s ease',
            '& fieldset': {
                borderColor: 'rgba(232, 163, 61, 0.2)',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(232, 163, 61, 0.4)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#E8A33D',
                borderWidth: '2px',
                boxShadow: '0 0 12px rgba(232, 163, 61, 0.3)',
            },
        },
        '& .MuiOutlinedInput-input': {
            color: '#ffffff',
            fontSize: '1rem',
            padding: '0.85rem 0.75rem',
            letterSpacing: '0.5px',
            [theme.breakpoints.up("xl")]: {
                fontSize: '1.2rem',
                padding: '1rem 0.9rem',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '1rem',
            [theme.breakpoints.up("xl")]: {
                fontSize: '1.2rem',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#E8A33D',
        },
    },
    keyIcon: {
        color: '#E8A33D',
        fontSize: '1.2rem',
        filter: 'drop-shadow(0 0 4px rgba(232, 163, 61, 0.3))',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.5rem',
        },
    },
    actions: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: '1rem',
        [theme.breakpoints.up("xl")]: {
            gap: '1.4rem',
        },
    },
    cancelButton: {
        padding: '0.4rem 2rem',
        backgroundColor: '#808080',
        color: '#ffffff',
        '&:hover': {
            backgroundColor: '#666666',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.45rem 2.4rem',
            fontSize: '1.2rem',
        },
    },
    confirmButton: {
        padding: '0.4rem 2rem',
        backgroundColor: '#E8A33D',
        color: '#ffffff',
        '&:hover': {
            backgroundColor: '#E8A33D',
        },
        '&:disabled': {
            backgroundColor: 'rgba(232, 163, 61, 0.3)',
            color: 'rgba(255, 255, 255, 0.4)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.45rem 2.4rem',
            fontSize: '1.2rem',
        },
    },
    lobbyCodeNotFoundContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '1rem',
        [theme.breakpoints.up("xl")]: {
            marginBottom: '1.2rem',
        },
    },
    lobbyCodeNotFound: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        width: '80%',
        padding: '0.4rem 0',
        backgroundColor: 'rgba(255, 59, 48, 0.12)',
        border: '1px solid rgba(255, 59, 48, 0.3)',
        borderRadius: '8px',
        animation: '$slideInShake 0.5s ease-out',
        [theme.breakpoints.up("xl")]: {
            padding: '0.5rem 0',
        },
    },
    errorIcon: {
        color: '#FF3B30',
        fontSize: '1.1rem',
        filter: 'drop-shadow(0 0 4px rgba(255, 59, 48, 0.4))',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.3rem',
        },
    },
    lobbyCodeNotFoundText: {
        color: '#FF3B30',
        fontSize: '0.9rem',
        fontWeight: 500,
        letterSpacing: '0.3px',
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.1rem',
        },
    },
    "@keyframes scaleInBounce": {
        '0%': { transform: 'scale(0.5)', opacity: 0 },
        '25%': { transform: 'scale(1.1)', opacity: 0.6 },
        '50%': { transform: 'scale(0.95)', opacity: 0.8 },
        '75%': { transform: 'scale(1.02)', opacity: 0.9 },
        '100%': { transform: 'scale(1)', opacity: 1 },
    },
    "@keyframes slideInShake": {
        '0%': { transform: 'translateY(-10px)', opacity: 0 },
        '50%': { transform: 'translateY(0)', opacity: 1 },
        '60%': { transform: 'translateX(-4px)' },
        '70%': { transform: 'translateX(4px)' },
        '80%': { transform: 'translateX(-2px)' },
        '90%': { transform: 'translateX(2px)' },
        '100%': { transform: 'translateX(0)' },
    },
}));

function JoinLobbyDialog({
    open,
    onCancel,
    joinLobbyByCode,
    lobbyCodeNotFound,
    lobbyFull,
}) {
    const classes = useStyles();
    const [lobbyCode, setLobbyCode] = useState('');

    if (!open) return null;

    const handleConfirm = () => {
        if (lobbyCode.trim()) {
            joinLobbyByCode(lobbyCode.trim());
            setLobbyCode('');
        }
    };

    const handleCancel = () => {
        setLobbyCode('');
        onCancel();
    };

    return (
        <div className={classes.overlay}>
            <div role="dialog" className={`${classes.content} ${classes.animateIn}`}>
                <div className={classes.header}>
                    <Typography className={classes.title}>
                        Join Lobby With Code
                    </Typography>
                </div>
                <div className={classes.inputContainer} style={{ marginBottom: (lobbyCodeNotFound || lobbyFull) ? '1rem' : '1.5rem' }}>
                    <TextField
                        className={classes.inputCodeLobby}
                        variant="outlined"
                        placeholder="Enter Lobby Code"
                        value={lobbyCode}
                        onChange={(e) => setLobbyCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && lobbyCode.trim()) {
                                e.preventDefault();
                                handleConfirm();
                            }
                        }}
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <VpnKeyIcon className={classes.keyIcon} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
                {(lobbyCodeNotFound || lobbyFull) && (
                    <div className={classes.lobbyCodeNotFoundContainer}>
                        <div className={classes.lobbyCodeNotFound}>
                            <ErrorOutlineIcon className={classes.errorIcon} />
                            <Typography className={classes.lobbyCodeNotFoundText}>
                                {lobbyFull ? 'The Lobby Is Full' : 'Invalid Lobby Code'}
                            </Typography>
                        </div>
                    </div>
                    
                )}
                <div className={classes.actions}>
                    <Button className={classes.cancelButton} onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button 
                        className={classes.confirmButton} 
                        onClick={handleConfirm}
                        disabled={!lobbyCode.trim()}
                    >
                        Enter
                    </Button>
                </div>
            </div>
        </div>
    );
}

JoinLobbyDialog.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  joinLobbyByCode: PropTypes.func.isRequired,
  lobbyCodeNotFound: PropTypes.bool,
  lobbyFull: PropTypes.bool,
};

export default JoinLobbyDialog;